import axios from "axios";
import { APP_PATH } from "../api/backendPath";
import { handleAccountDeactivated } from "../utils/accountDeactivated";

class SessionService {
  constructor() {
    this.validationInterval = null;
    this.refreshTimer = null;
    this.accountCheckInterval = null;
    this.ACCOUNT_CHECK_INTERVAL = 30 * 1000;
    this.VALIDATION_INTERVAL = 10 * 60 * 1000; // Check every 10 minutes
    this.SESSION_WARNING_TIME = 30 * 60 * 1000; // Warn 30 minutes before session expiry
    this.DOCTOR_SESSION_DURATION = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds
    this.REFRESH_BEFORE_EXPIRY = 2 * 60 * 1000; // Refresh 2 minutes before token expires (was 12 seconds)
    this.isRefreshing = false; // Prevent multiple simultaneous refresh attempts
  }

  /**
   * Start session validation monitoring
   */
  startSessionMonitoring() {
    // Clear any existing interval
    this.stopSessionMonitoring();

    console.log("🚀 Starting session monitoring...");

    // Start validation interval
    this.validationInterval = setInterval(() => {
      this.validateSession();
    }, this.VALIDATION_INTERVAL);

    this.accountCheckInterval = setInterval(() => {
      this.checkAccountActive();
    }, this.ACCOUNT_CHECK_INTERVAL);

    // Start proactive token refresh timer
    this.scheduleTokenRefresh();

    // Perform initial validation after a short delay
    setTimeout(() => {
      this.validateSession();
    }, 5000); // 5 seconds after start
  }

  /**
   * Stop session validation monitoring
   */
  stopSessionMonitoring() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = null;
      console.log("⏹️ Stopped session monitoring");
    }
    if (this.accountCheckInterval) {
      clearInterval(this.accountCheckInterval);
      this.accountCheckInterval = null;
    }
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      console.log("⏹️ Stopped token refresh timer");
    }
    this.isRefreshing = false;
  }

  async checkAccountActive() {
    const access_token = sessionStorage.getItem("usr_1a2b3c");
    if (!access_token) return;
    try {
      await axios.get(process.env.REACT_APP_API_URL + APP_PATH.labProfile, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
    } catch (error) {
      handleAccountDeactivated(error);
    }
  }

  /**
   * Validate current session
   */
  async validateSession() {
    const access_token = sessionStorage.getItem("usr_1a2b3c");
    const refresh_token = sessionStorage.getItem("usr_4d5e6f");

    // Skip validation if not authenticated
    if (!access_token || !refresh_token) {
      console.log("⏭️ Skipping session validation - no tokens found");
      return;
    }

    // First check if the session has actually expired (2 days)
    if (this.isSessionExpired()) {
      console.log("⏰ Session has expired (2 days limit reached)");
      this.handleSessionExpiry("Session expired - 2 days limit reached");
      return;
    }

    // Check if token is about to expire and refresh proactively
    const expirationTime = this.getTokenExpiration(access_token);
    if (expirationTime) {
      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;

      // If token expires in less than 3 minutes, refresh it now
      if (timeUntilExpiry <= 3 * 60 * 1000 && timeUntilExpiry > 0) {
        console.log("⏰ Token expires soon, refreshing proactively...");
        await this.attemptTokenRefresh(sessionStorage.getItem("usr_4d5e6f"));
        return;
      }

      // If token is already expired, refresh immediately
      if (timeUntilExpiry <= 0) {
        console.log("⏰ Token has expired, refreshing immediately...");
        await this.attemptTokenRefresh(sessionStorage.getItem("usr_4d5e6f"));
        return;
      }
    }

    try {
      console.log("🔍 Validating session...");

      // Use the validate-token endpoint
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.validateToken,
        { token: access_token },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            "Content-Type": "application/json",
          },
          timeout: 10000, // 10 second timeout
        },
      );

      // Check if session is still valid
      if (!response?.data?.data?.valid) {
        console.log("⚠️ Token is invalid, attempting refresh...");
        await this.attemptTokenRefresh(sessionStorage.getItem("usr_4d5e6f"));
      } else {
        console.log("✅ Session is valid");
      }
    } catch (error) {
      console.warn("⚠️ Session validation failed:", error);

      // Only try to refresh if it's a 401 error (token expired)
      if (error?.response?.status === 401) {
        await this.attemptTokenRefresh(sessionStorage.getItem("usr_4d5e6f"));
      } else if (
        error.message?.includes("Network Error") ||
        error.message?.includes("timeout")
      ) {
        // Don't handle network errors as session expiry
        console.warn(
          "🌐 Network error during session validation, skipping expiry handling",
        );
      } else {
        // For other errors, don't try to refresh - just log and continue
        console.warn(
          "❌ Session validation failed with non-401 error, not attempting refresh",
        );
      }
    }
  }

  /**
   * Decode JWT token to get expiration time
   */
  getTokenExpiration(token) {
    if (!token) return null;
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      const payload = JSON.parse(jsonPayload);
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      console.warn("Failed to decode token:", error);
      return null;
    }
  }

  /**
   * Schedule proactive token refresh before expiration
   */
  scheduleTokenRefresh() {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const access_token = sessionStorage.getItem("usr_1a2b3c");
    const refresh_token = sessionStorage.getItem("usr_4d5e6f");

    if (!access_token || !refresh_token) {
      return;
    }

    const expirationTime = this.getTokenExpiration(access_token);
    if (!expirationTime) {
      console.warn(
        "⚠️ Could not determine token expiration, will validate on next interval",
      );
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;
    const refreshTime = timeUntilExpiry - this.REFRESH_BEFORE_EXPIRY;

    console.log(
      `⏰ Token expires in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes, will refresh in ${Math.round(refreshTime / 1000 / 60)} minutes`,
    );

    // If token expires very soon or already expired, refresh immediately
    if (refreshTime <= 0 || timeUntilExpiry <= 0) {
      console.log(
        "🔄 Token expired or expiring soon, refreshing immediately...",
      );
      this.attemptTokenRefresh(refresh_token);
      return;
    }

    // If refresh time is too far in the future (more than 12 minutes),
    // set a reasonable maximum delay (10 minutes) and reschedule
    const maxDelay = 10 * 60 * 1000; // 10 minutes max
    const delay = Math.min(refreshTime, maxDelay);

    console.log(
      `⏰ Scheduling token refresh in ${Math.round(delay / 1000 / 60)} minutes`,
    );

    this.refreshTimer = setTimeout(() => {
      const currentRefreshToken = sessionStorage.getItem("usr_4d5e6f");
      if (currentRefreshToken) {
        this.attemptTokenRefresh(currentRefreshToken);
      }
    }, delay);
  }

  /**
   * Attempt to refresh the access token
   */
  async attemptTokenRefresh(refreshToken) {
    if (!refreshToken) {
      this.handleSessionExpiry("No refresh token available");
      return false;
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      console.log("🔄 Token refresh already in progress, skipping...");
      return false;
    }

    this.isRefreshing = true;

    try {
      console.log("🔄 Attempting to refresh access token...");

      const access_token = sessionStorage.getItem("usr_1a2b3c");
      // Always read the latest refresh token from storage, not the argument
      // (the argument may be stale if called from a closure)
      const currentRefreshToken =
        sessionStorage.getItem("usr_4d5e6f") || refreshToken;

      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.refreshToken,
        { refresh_token: currentRefreshToken },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${access_token}`,
            "User-Refresh-Token": currentRefreshToken,
            "Content-Type": "application/json",
          },
          timeout: 15000,
        },
      );

      if (
        response?.data?.data?.access_token &&
        response?.data?.data?.refresh_token
      ) {
        sessionStorage.setItem("usr_1a2b3c", response.data.data.access_token);
        sessionStorage.setItem("usr_4d5e6f", response.data.data.refresh_token);
        console.log("✅ Token refreshed successfully");
        this.isRefreshing = false;
        this.scheduleTokenRefresh();
        return true;
      } else {
        console.warn("⚠️ Token refresh response invalid");
        this.isRefreshing = false;
        this.handleSessionExpiry("Invalid refresh response");
        return false;
      }
    } catch (error) {
      console.warn("❌ Token refresh failed:", error);
      this.isRefreshing = false;

      if (
        error?.response?.data?.error_code === "AUTH_010" ||
        error?.response?.data?.message?.includes(
          "Invalid or expired refresh token",
        ) ||
        error?.response?.status === 400 ||
        error?.response?.status === 401
      ) {
        console.error("🚨 Refresh token is invalid or expired");
        this.handleSessionExpiry("Refresh token expired - session expired");
        return false;
      }

      // Network/server errors — retry after delay, don't logout
      console.warn("⚠️ Token refresh failed with non-auth error, will retry");
      setTimeout(() => {
        const latestToken = sessionStorage.getItem("usr_4d5e6f");
        if (latestToken && !this.isRefreshing) {
          this.attemptTokenRefresh(latestToken);
        }
      }, 5000);
      return false;
    }
  }

  /**
   * Handle session expiry
   */
  handleSessionExpiry(reason) {
    console.log("🚨 Session expired:", reason);

    // Stop monitoring
    this.stopSessionMonitoring();

    // Clear session and redirect
    this.clearSessionAndRedirect();
  }

  /**
   * Clear session and redirect to login
   */
  clearSessionAndRedirect() {
    // Stop monitoring
    this.stopSessionMonitoring();

    // Clear session start time
    try {
      sessionStorage.removeItem("sessionStartTime");
    } catch (error) {
      console.warn("Failed to clear session start time:", error);
    }

    // Clear all session storage
    sessionStorage.clear();

    // Redirect to login page
    window.location.href = "/";
  }

  /**
   * Check if session is about to expire based on session creation time
   * This checks the actual session expiry (2 days) not token expiry (15 minutes)
   */
  isSessionAboutToExpire() {
    const sessionStartTime = this.getSessionStartTime();
    if (!sessionStartTime) return false;

    const now = Date.now();
    const timeSinceStart = now - sessionStartTime;
    const timeUntilExpiry = this.DOCTOR_SESSION_DURATION - timeSinceStart;

    return timeUntilExpiry <= this.SESSION_WARNING_TIME;
  }

  /**
   * Get session start time from sessionStorage
   */
  getSessionStartTime() {
    try {
      const stored = sessionStorage.getItem("sessionStartTime");
      if (stored) {
        return parseInt(stored, 10);
      }
      return null;
    } catch (error) {
      console.warn("Failed to get session start time:", error);
      return null;
    }
  }

  /**
   * Check if session has actually expired (2 days)
   */
  isSessionExpired() {
    const sessionStartTime = this.getSessionStartTime();
    if (!sessionStartTime) {
      // No session start time means we can't determine expiry — don't force logout
      // The token itself will be validated by the server on next API call
      return false;
    }

    const now = Date.now();
    const timeSinceStart = now - sessionStartTime;

    return timeSinceStart >= this.DOCTOR_SESSION_DURATION;
  }

  /**
   * Set session start time (call this on login)
   */
  setSessionStartTime() {
    try {
      sessionStorage.setItem("sessionStartTime", Date.now().toString());
      console.log("✅ Session start time set");
    } catch (error) {
      console.warn("Failed to set session start time:", error);
    }
  }

  /**
   * Force token refresh (can be called manually)
   */
  async forceTokenRefresh() {
    const refresh_token = sessionStorage.getItem("usr_4d5e6f");
    if (refresh_token) {
      return await this.attemptTokenRefresh(refresh_token);
    }
    return false;
  }
}

// Export singleton instance
export const sessionService = new SessionService();
